import { useState, useEffect, useRef, useCallback } from "react";
import { LoadPanel } from "devextreme-react";
import Splitter, { Item as SplitterItem } from "devextreme-react/splitter";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { Vendor, getVendors } from "../../../api/support";
import { MOCK_VENDORS } from "../vendors/vendor-mock";
import { VendorInfoPane } from "../vendors/VendorInfoPane";
import { CustomDataGrid } from "../CustomDataGrid";
import "../messages/TicketPage.css";

// Organizasyonlar (musteri firmalar) — TicketPage duzeni:
// ust panelde CustomDataGrid liste, alt panelde cok-sekmeli (MDI) firma detayi.
// Satira tiklayinca altta secili firma VendorInfoPane olarak acilir.
type VendorTab = { ref: string; vendorRef: string; title: string };
const createTabId = () => `vtab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function OrganizationsPage() {
    const { t } = useTranslation();

    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [loading, setLoading] = useState(true);
    const [gridKey, setGridKey] = useState(0);

    // MDI sekmeler
    const [tabs, setTabs] = useState<VendorTab[]>([]);
    const [activeTabId, setActiveTabId] = useState<string | null>(null);
    const [topCollapsed, setTopCollapsed] = useState(false);
    const [bottomCollapsed, setBottomCollapsed] = useState(false);

    const activeTab = tabs.find(tb => tb.ref === activeTabId) || null;

    const toggleBottom = useCallback(() => {
        setBottomCollapsed(prev => !prev);
        setTopCollapsed(false);
        setTimeout(() => setGridKey(k => k + 1), 250);
    }, []);

    const toggleTop = useCallback(() => {
        setTopCollapsed(prev => !prev);
        setBottomCollapsed(false);
        setTimeout(() => setGridKey(k => k + 1), 250);
    }, []);

    // ── Load vendors ──────────────────────────────────────────────────────────
    const load = useCallback(() => {
        setLoading(true);
        getVendors(false)
            .then(list => setVendors(list.length ? list : MOCK_VENDORS))
            .catch(() => setVendors(MOCK_VENDORS))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    // ── Tab yonetimi ──────────────────────────────────────────────────────────
    const openVendorInTab = useCallback((vendor: Vendor) => {
        setTabs(prev => {
            const existing = prev.find(tb => tb.vendorRef === String(vendor.ref));
            if (existing) { setActiveTabId(existing.ref); return prev; }
            const tabId = createTabId();
            setActiveTabId(tabId);
            return [...prev, { ref: tabId, vendorRef: String(vendor.ref), title: vendor.name || `#${vendor.ref}` }];
        });
    }, []);

    const closeTab = useCallback((tabId: string) => {
        setTabs(prev => {
            if (prev.length <= 1) { setActiveTabId(null); return []; }
            const idx = prev.findIndex(tb => tb.ref === tabId);
            const next = prev.filter(tb => tb.ref !== tabId);
            setActiveTabId(cur => cur === tabId ? (next[Math.max(0, idx - 1)] || next[0])?.ref || null : cur);
            return next;
        });
    }, []);

    // Satir secimi: aktif sekme varsa onu degistir, yoksa yeni sekme ac (TicketPage davranisi).
    const selectVendor = useCallback((vendor: Vendor) => {
        if (!activeTabId || tabs.length === 0) { openVendorInTab(vendor); return; }
        setTabs(prev => prev.map(tb => tb.ref === activeTabId
            ? { ...tb, vendorRef: String(vendor.ref), title: vendor.name || `#${vendor.ref}` }
            : tb));
    }, [activeTabId, tabs.length, openVendorInTab]);

    const handleRowClick = (data: Vendor) => selectVendor(data);
    const handleFocusedRowChanged = useCallback((e: any) => {
        if (e?.row?.data) selectVendor(e.row.data);
    }, [selectVendor]);

    const columns = [
        { dataField: "ref", caption: "ID", width: 120, alignment: "left" as const, dataType: "string" as const },
        { dataField: "name", caption: t("vendorName"), width: 200, allowGrouping: true },
        { dataField: "contact_person", caption: t("contactPerson"), width: 160 },
        { dataField: "email", caption: t("email"), width: 220 },
        { dataField: "phone", caption: t("phone"), width: 150 },
        { dataField: "city", caption: t("city"), width: 140, allowGrouping: true },
        { dataField: "country", caption: t("country"), width: 140, allowGrouping: true },
        {
            dataField: "is_active",
            caption: t("status"),
            width: 110,
            alignment: "center" as const,
            cellRender: (d: any) => {
                const active = d.value !== false;
                const color = active ? "#10B981" : "#9CA3AF";
                return (
                    <span style={{ background: `${color}1f`, color, fontWeight: 600, fontSize: 12, padding: "2px 10px", borderRadius: 999 }}>
                        {active ? t("active") : t("passive")}
                    </span>
                );
            },
        },
        { dataField: "", caption: "", width: undefined, allowSorting: false, allowFiltering: false },
    ];

    return (
        <div className="ticket-page-root flex flex-col h-full w-full" style={{ paddingBottom: 30 }}>
            <LoadPanel visible={loading} />

            <div className="ticket-splitter-wrap flex-1 min-h-0">
                <Splitter orientation="vertical">
                    {/* Üst Pane - Firma listesi */}
                    <SplitterItem size="40%" minSize="200px" collapsed={topCollapsed}>
                        <CustomDataGrid
                            key={`org-grid-${gridKey}`}
                            className="custom-grid compact-grid"
                            data={vendors}
                            columns={columns}
                            keyExpr="ref"
                            height="100%"
                            t={t}
                            noDataText={t("noDataText")}
                            onRefresh={load}
                            onRowClick={handleRowClick}
                            onFocusedRowChanged={handleFocusedRowChanged}
                            focusedRowKey={activeTab?.vendorRef ?? undefined}
                            columnChooserEnabled
                            filterRowVisible={false}
                            rowAlternationEnabled={true}
                            allowColumnReordering
                            exportEnabled
                            groupPanelVisible
                            additionalToolbarItems={[
                                {
                                    location: "after",
                                    render: () => (
                                        <button
                                            type="button"
                                            className="splitter-collapse-btn"
                                            title={t("closeBottomPane")}
                                            onClick={toggleBottom}
                                        >
                                            <FontAwesomeIcon icon={bottomCollapsed ? faChevronUp : faChevronDown} />
                                        </button>
                                    ),
                                },
                            ]}
                        />
                    </SplitterItem>

                    {/* Alt Pane - Sekmeli firma detayi */}
                    <SplitterItem size="60%" minSize="300px" collapsed={bottomCollapsed}>
                        <div className="h-full flex flex-col overflow-hidden" style={{ background: "var(--background-color)", padding: "0 12px" }}>
                            {tabs.length > 0 ? (
                                <>
                                    {/* Tab Bar */}
                                    <div className="detail-header-tabs shrink-0">
                                        {tabs.map(tab => (
                                            <div
                                                key={tab.ref}
                                                className={`detail-header-tab ${tab.ref === activeTabId ? "active" : ""}`}
                                                title={tab.title}
                                                onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); closeTab(tab.ref); } }}
                                            >
                                                <button type="button" className="detail-header-tab-label" onClick={() => setActiveTabId(tab.ref)}>
                                                    <i className="dx-icon-group detail-header-tab-icon" />
                                                    <span className="detail-header-tab-title-text">{tab.title}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="detail-header-tab-close"
                                                    onClick={(e) => { e.stopPropagation(); closeTab(tab.ref); }}
                                                    title={t("closeTab")}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className="splitter-collapse-btn"
                                            style={{ marginLeft: "auto" }}
                                            title={t("closeTopPane")}
                                            onClick={toggleTop}
                                        >
                                            <FontAwesomeIcon icon={topCollapsed ? faChevronDown : faChevronUp} />
                                        </button>
                                    </div>

                                    {/* Tab Content */}
                                    <div className="flex-1 min-h-0">
                                        {tabs.map(tab => (
                                            <div key={tab.ref} className="h-full" style={{ display: tab.ref === activeTabId ? "block" : "none" }}>
                                                <VendorInfoPane vendorRef={tab.vendorRef} />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="detail-header-tabs shrink-0">
                                        <button
                                            type="button"
                                            className="splitter-collapse-btn"
                                            style={{ marginLeft: "auto" }}
                                            title={t("closeTopPane")}
                                            onClick={toggleTop}
                                        >
                                            <FontAwesomeIcon icon={topCollapsed ? faChevronDown : faChevronUp} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-center flex-1" style={{ color: "var(--subtitle-text-color)" }}>
                                        {t("selectTicketForDetail")}
                                    </div>
                                </>
                            )}
                        </div>
                    </SplitterItem>
                </Splitter>
            </div>
        </div>
    );
}
